import { LOCATION_MODE } from '@/types/location-mode';
import type { CreateServiceTemplateDto, ServiceTemplate } from '@/hooks/use-service-templates';
import { emptyServiceForm, type ServiceFormData } from '@/lib/validators/service';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import type { Service } from '@/types/service';
import { formatCurrency } from '@/utils/format-currency';
import { parseCurrency } from '@/utils/parse-currency';

/**
 * O serviço salvo virando formulário — o espelho de `toServicePayload`
 * (`hooks/use-services.ts`). Duplicar um serviço, abrir a mesma tela para editar
 * e reaplicar um modelo partem todos deste mapeamento, então não podem divergir.
 */
export function serviceToForm(service: Service): ServiceFormData {
  const hasPrice = service.defaultPrice != null;
  return {
    ...emptyServiceForm,
    name: service.name ?? '',
    description: service.description || '',
    isActive: service.isActive ?? true,
    address: service.address || '',
    locationMode:
      service.locationMode ?? (service.address ? LOCATION_MODE.PROVIDER_SPACE : undefined),
    hasFixedPrice: hasPrice,
    defaultPrice: hasPrice ? formatCurrency(service.defaultPrice as number) : '',
    priceDisplayMode: service.priceDisplayMode ?? PRICE_DISPLAY_MODE.SHOW,
    allowedPaymentMethods:
      service.allowedPaymentMethods?.length > 0
        ? (service.allowedPaymentMethods as ServiceFormData['allowedPaymentMethods'])
        : emptyServiceForm.allowedPaymentMethods,
    isSchedulable: service.durationMinutes != null,
    durationMinutes: service.durationMinutes ?? null,
    slotCapacity: service.slotCapacity ?? 1,
    isSubscription: !!service.isSubscription,
    subscriptionQuota: service.subscriptionQuota ?? emptyServiceForm.subscriptionQuota,
    subscriptionQuotaPeriod:
      service.subscriptionQuotaPeriod ?? emptyServiceForm.subscriptionQuotaPeriod,
    recurringBookingMode: service.recurringBookingMode ?? RECURRING_BOOKING_MODE.OFF,
    recurringBillingInterval:
      service.recurringBillingInterval ?? emptyServiceForm.recurringBillingInterval,
    recurringHorizonDays: service.recurringHorizonDays ?? emptyServiceForm.recurringHorizonDays,
  };
}

/**
 * O modelo virando formulário — a configuração inteira, não só a capa.
 *
 * Enquanto isso copiava apenas nome, descrição, endereço, preço e métodos de
 * pagamento, "aplicar modelo" entregava meio serviço: a duração, as vagas e a
 * recorrência ficavam para o prestador redigitar, e reaplicar o modelo que ele
 * mesmo salvou não reproduzia o serviço de origem.
 */
export function templateToServiceForm(template: ServiceTemplate): ServiceFormData {
  return {
    ...emptyServiceForm,
    name: template.name,
    description: template.description || '',
    address: template.address || '',
    locationMode: template.address ? LOCATION_MODE.PROVIDER_SPACE : undefined,
    hasFixedPrice: template.defaultPrice != null,
    defaultPrice: template.defaultPrice != null ? formatCurrency(template.defaultPrice) : '',
    priceDisplayMode: template.priceDisplayMode ?? PRICE_DISPLAY_MODE.SHOW,
    allowedPaymentMethods:
      template.allowedPaymentMethods?.length > 0
        ? (template.allowedPaymentMethods as ServiceFormData['allowedPaymentMethods'])
        : emptyServiceForm.allowedPaymentMethods,
    isSchedulable: template.durationMinutes != null,
    durationMinutes: template.durationMinutes ?? null,
    slotCapacity: template.slotCapacity ?? 1,
    // O modelo carrega a assinatura inteira — a marca e a cota. Sem a cota,
    // aplicar o modelo "Plano 3x por semana" devolvia um serviço que não sabia
    // quantas sessões entrega, e o prestador redigitava o que ele já tinha
    // salvo.
    isSubscription: !!template.isSubscription,
    subscriptionQuota: template.subscriptionQuota ?? emptyServiceForm.subscriptionQuota,
    subscriptionQuotaPeriod:
      template.subscriptionQuotaPeriod ?? emptyServiceForm.subscriptionQuotaPeriod,
    recurringBookingMode: template.recurringBookingMode ?? RECURRING_BOOKING_MODE.OFF,
    recurringBillingInterval:
      template.recurringBillingInterval ?? emptyServiceForm.recurringBillingInterval,
    recurringHorizonDays: template.recurringHorizonDays ?? emptyServiceForm.recurringHorizonDays,
  };
}

/**
 * O formulário virando modelo. É o caminho de volta do `templateToServiceForm`:
 * salvar como modelo e reaplicar tem que devolver o mesmo serviço.
 *
 * A cadência e a validade só viajam quando a recorrência está ligada — mandar
 * `recurringBillingInterval` com o modo `OFF` é justamente a incoerência que o
 * backend recusa.
 */
export function serviceFormToTemplateDto(data: ServiceFormData): CreateServiceTemplateDto {
  const recurring =
    !!data.isSubscription ||
    (!!data.recurringBookingMode && data.recurringBookingMode !== RECURRING_BOOKING_MODE.OFF);
  // Assinatura é sempre mensal e não vence: guardar cadência e validade no
  // modelo seria salvar o que o backend zera na volta.

  return {
    name: data.name,
    description: data.description,
    defaultPrice: data.hasFixedPrice ? parseCurrency(data.defaultPrice) : undefined,
    priceDisplayMode: data.hasFixedPrice ? data.priceDisplayMode : undefined,
    address: data.locationMode === LOCATION_MODE.PROVIDER_SPACE ? data.address : undefined,
    allowedPaymentMethods: data.allowedPaymentMethods,
    durationMinutes: data.durationMinutes ?? null,
    slotCapacity: data.slotCapacity ?? 1,
    isSubscription: !!data.isSubscription,
    subscriptionQuota: data.isSubscription ? data.subscriptionQuota : null,
    subscriptionQuotaPeriod: data.isSubscription ? data.subscriptionQuotaPeriod : null,
    recurringBookingMode: data.isSubscription
      ? RECURRING_BOOKING_MODE.AUTO
      : (data.recurringBookingMode ?? RECURRING_BOOKING_MODE.OFF),
    recurringBillingInterval: recurring ? data.recurringBillingInterval : null,
    recurringHorizonDays: recurring && !data.isSubscription ? data.recurringHorizonDays : null,
  };
}
